import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import VTooltip from "v-tooltip";
import * as Sentry from "@sentry/vue";
import CKEditor from "@ckeditor/ckeditor5-vue2";
import VueRouter from "vue-router";
import { FontBackgroundColorBlock } from "./plugins/ckeditor/FontBackgroundColorBlock";
import {
  setCKEditorPrincipalColorPalette,
  setCKEditorSourceEditing
} from "@/helpers/commonHelper";
import BaseDataTable from "./components/Common/BaseDataTable/BaseDataTable.vue";
import TextDiffViewer from "./components/Common/TextDiffViewer/TextDiffViewer.vue";

const { isNavigationFailure } = VueRouter;

const isUnauthorizedError = error => {
  const status =
    error?.response?.status ??
    error?.status ??
    error?.statusCode ??
    error?.request?.status;

  if (status === 401) return true;

  const message = error?.message ?? error?.toString?.() ?? "";
  return (
    message === "Error: Not Authorised" ||
    message === "Request failed with status code 401"
  );
};

const isNetworkError = error => {
  if (error?.code === "ERR_NETWORK") return true;

  const message = error?.message ?? error?.toString?.() ?? "";
  return /network error/i.test(message);
};

const isNavigationFailureError = error => {
  if (!error) return false;
  if (isNavigationFailure(error)) return true;

  const message = error?.message ?? error?.toString?.() ?? "";
  return /navigation duplicated|avoided redundant navigation/i.test(message);
};

const hasIgnoredErrorMessage = event => {
  const messages = [
    event?.message,
    event?.logentry?.formatted,
    ...(event?.exception?.values?.map(({ value }) => value) ?? [])
  ];
  return messages.some(message =>
    /status code 401|not authorised|network error|navigation duplicated|avoided redundant navigation/i.test(
      message ?? ""
    )
  );
};

const shouldIgnoreSentryError = (event, error) =>
  isUnauthorizedError(error) ||
  isNetworkError(error) ||
  isNavigationFailureError(error) ||
  hasIgnoredErrorMessage(event);

Sentry.init({
  Vue,
  dsn:
    "https://04c36bcf66bc085cfc88622e84e2ab55@o4509073123901440.ingest.de.sentry.io/4509093487050832",
  tracesSampleRate: 1.0,
  beforeSend(event, hint) {
    const originalException = hint?.originalException;
    if (shouldIgnoreSentryError(event, originalException)) {
      return null;
    }

    return event;
  }
});

Vue.use(CKEditor);
Vue.use(VTooltip);

// Register global BaseDataTable component
Vue.component("base-data-table", BaseDataTable);
Vue.component("v-data-table", BaseDataTable);
Vue.component("TextDiffViewer", TextDiffViewer);

import "./Variables.scss";
import moment from "moment";
import axios from "axios";
import * as fmt from "@/utils/format";

// Registriere axios als Vue prototype
Vue.prototype.$axios = axios;

Object.entries(fmt).forEach(([name, fn]) => {
  Vue.filter(name, fn);
});

let axiosRetries = 0;

axios.interceptors.response.use(null, error => {
  if (error.config && error.response?.status > 400 && axiosRetries < 3) {
    axiosRetries++;
    return axios.request(error.config);
  } else {
    axiosRetries = 0;
    return Promise.reject(error);
  }
});

Array.prototype.asyncForEach = async function(callback) {
  for (let index = 0; index < this.length; index++) {
    await callback(this[index], index, this);
  }
};

String.prototype.dateFormat = function(format = "DD.MM.YYYY HH:mm") {
  return moment(this).format(format);
};

// dashboard-datepicker saves 16.03.2020 like 2020-03-16T00:00:00.000Z(12 o'clock/midnight GREENWICH)
// INSTEAD of 2020-03-15T23:00:00.000Z(12 o'clock/midnight BERLIN winter)
// to avoid wrong day for old user inputs use dateFormatUtc for user-defined dates
String.prototype.dateFormatUtc = function(format = "DD.MM.YYYY HH:mm") {
  return moment.utc(this).format(format);
};

Array.prototype.remove = function(item) {
  const index = this.indexOf(item);
  if (index !== -1) this.splice(this.indexOf(item), 1);
  return this;
};

Array.prototype.replace = function(item, replaceItem) {
  const index = this.indexOf(item);
  this.remove(item);
  this.splice(index, 0, replaceItem);
};

String.random = function(length = 40) {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};

// prettier-ignore
// eslint-disable-next-line no-undef
const {ClassicEditor, Essentials, Paragraph, Heading, Bold, Italic, Underline, FontColor, FontBackgroundColor, Clipboard, Undo, List, BlockQuote, HorizontalLine, Table, RemoveFormat, PasteFromOffice, Alignment, Link, FindAndReplace, SourceEditing, GeneralHtmlSupport } = CKEDITOR;

class EnableCKEditorTabKeyInsertion {
  constructor(editor) {
    this.editor = editor;
  }

  static get pluginName() {
    return "EnableCKEditorTabKeyInsertion";
  }

  init() {
    const { editor } = this;

    editor.keystrokes.set("Tab", (keyEvtData, cancel) => {
      keyEvtData.preventDefault();
      editor.model.change(writer => {
        editor.model.insertContent(writer.createText("\t"));
      });
      cancel();
    });
  }
}

Vue.prototype.$editor = ClassicEditor;
Vue.prototype.$editorConfig = {
  "editor-url": "https://cdn.eberl-online.cloud/ckeditor5/ckeditor5.js",
  extraPlugins: [EnableCKEditorTabKeyInsertion],
  plugins: [
    Essentials,
    Paragraph,
    Heading,
    Bold,
    Italic,
    Underline,
    FontColor,
    FontBackgroundColor,
    FontBackgroundColorBlock,
    Clipboard,
    Undo,
    List,
    BlockQuote,
    HorizontalLine,
    Table,
    RemoveFormat,
    PasteFromOffice,
    Alignment,
    Link,
    FindAndReplace,
    SourceEditing,
    GeneralHtmlSupport
  ],
  heading: {
    options: [
      { model: "paragraph", title: "Absatz" },
      {
        model: "heading2",
        view: "h2",
        title: "H2"
      },
      {
        model: "heading3",
        view: "h3",
        title: "H3"
      },
      {
        model: "heading4",
        view: "h4",
        title: "H4"
      }
    ]
  },
  htmlSupport: {
    allow: [
      {
        name: /.*/,
        styles: {
          "background-color": true
        }
      }
    ]
  },
  shouldNotGroupWhenFull: true,
  licenseKey: "GPL",
  language: "de",
  toolbar: [
    "undo",
    "redo",
    "|",
    "findAndReplace",
    "|",
    "heading",
    "bold",
    "italic",
    "underline",
    "fontColor",
    "fontBackgroundColor",
    "bulletedList",
    "numberedList",
    "blockQuote",
    "insertTable",
    "horizontalLine",
    "removeFormat",
    "|",
    "alignment:left",
    "alignment:center",
    "alignment:right",
    "alignment:justify",
    "|",
    "link",
    "unlink"
  ]
};
Vue.prototype.$mainLightBlue = "#00dadb";
Vue.prototype.$mainRed = "#A60F0F";
Vue.prototype.$mainPeacock = "#00636C";
Vue.prototype.$mainGray = "#7a7a7a";
Vue.prototype.$mainGrey = "#7a7a7a";
Vue.prototype.$mainBlack = "#121212";
Vue.prototype.$mainDarkGreen = "#5D7114";
Vue.prototype.$mainDarkGrey = "#444444";
Vue.prototype.$mainBlue = "#314D57";
Vue.prototype.$mainBeige = "#e2d7af";
Vue.prototype.$mainYellow = "#e5c14a";
Vue.prototype.$mainLightGrey = "#f6f6f6";
Vue.prototype.$secondaryLightGrey = "#eeeeee";
Vue.prototype.$pageLimitOptions = [10, 25, 50, 100];

// Globale Snackbar-Funktion
Vue.prototype.$snackbar = function(message, color = "success") {
  store.commit("SET_SNACKBAR_TEXT", message);
  store.commit("SET_SNACKBAR_COLOR", color);
  store.commit("SET_SNACKBAR", true);
  setTimeout(() => {
    store.commit("SET_SNACKBAR", false);
  }, 3000);
};
Vue.config.productionTip = false;
Vue.config.ignoredElements = [
  ...(Vue.config.ignoredElements || []),
  "eo-chatbot-widget"
];
Vue.prototype.REGION_PRINCIPAL = "00000000-0000-0000-0000-100000000000";

Vue.config.errorHandler = function(err) {
  if (shouldIgnoreSentryError(null, err)) {
    return;
  }
  // handle error
  // `info` is a Vue-specific error info, e.g. which lifecycle hook
  // the error was found in. Only available in 2.2.0+
  // add vm, info as parameter if needed
  // console.log(err, vm, info, "global error handler");
  Sentry.captureException(err);
};

// Ensure CKEditor is configured correctly on init.
setCKEditorPrincipalColorPalette();
setCKEditorSourceEditing();

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App)
}).$mount("#app");
